import { isNotNil } from "ramda-adjunct";
import React, { PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

type BreakPenaltyContextValue = {
  /**
   * Form path of the interval settings, every field lives under `${path}.<field>`
   */
  path: string;
  disabled?: boolean;
  /**
   * Whether the penalty length is entered by hand instead of following the required break length
   */
  customPenalty: boolean;
  /**
   * Switches the penalty length between a custom value and the required break length
   */
  setCustomPenalty: (enabled: boolean) => void;
  /**
   * Keeps the penalty length on a newly entered break length as long as it is not custom
   */
  syncPenaltyWithBreak: (breakMinutes: number | null) => void;
};

const BreakPenaltyContext = React.createContext<BreakPenaltyContextValue | undefined>(undefined);
BreakPenaltyContext.displayName = "BreakPenalty";

/**
 * Holds what the break penalty inputs share: the settings path and the link between the required
 * break length and the penalty. A missed break is penalized by its own required length until the
 * user asks for a custom penalty, so the two values move together while the switch is off.
 */
export const BreakPenaltyProvider = ({
  children,
  path,
  disabled,
}: PropsWithChildren<{ path: string; disabled?: boolean }>) => {
  const { getValues, setValue } = useFormContext();
  const breakPath = `${path}.breakMinutes`;
  const penaltyPath = `${path}.penaltyMinutes`;

  const [customPenalty, setCustomPenaltyState] = useState<boolean>(() => {
    const penalty = getValues(penaltyPath);
    return isNotNil(penalty) && penalty !== getValues(breakPath);
  });

  const writePenalty = useCallback(
    (minutes: number | null) => setValue(penaltyPath, minutes, { shouldDirty: true, shouldValidate: true }),
    [penaltyPath, setValue]
  );

  const setCustomPenalty = useCallback(
    (enabled: boolean) => {
      setCustomPenaltyState(enabled);
      if (!enabled) {
        writePenalty(getValues(breakPath));
      }
    },
    [breakPath, getValues, writePenalty]
  );

  const syncPenaltyWithBreak = useCallback(
    (breakMinutes: number | null) => {
      if (!customPenalty) {
        writePenalty(breakMinutes);
      }
    },
    [customPenalty, writePenalty]
  );

  const value = useMemo(
    () => ({ path, disabled, customPenalty, setCustomPenalty, syncPenaltyWithBreak }),
    [customPenalty, disabled, path, setCustomPenalty, syncPenaltyWithBreak]
  );

  return <BreakPenaltyContext.Provider value={value}>{children}</BreakPenaltyContext.Provider>;
};

export function useBreakPenalty(): BreakPenaltyContextValue {
  const context = useContext(BreakPenaltyContext);
  if (!context) {
    throw new Error("useBreakPenalty must be used inside a BreakPenaltyProvider");
  }
  return context;
}
